import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/**
 * Shortcode → Unicode emoji resolution.
 *
 * ## Why a local map rather than flarum/emoji's
 *
 * flarum/emoji exposes the same data, but only through a dynamic `import()`, so it
 * lives in a lazily loaded chunk that is fetched when the composer autocomplete
 * first needs it — possibly never. Reading it through `flarum.reg.get()` would
 * therefore return `undefined` most of the time, which is the same trap that made
 * `forum/ForumApplication` unusable at initializer time.
 *
 * We import `simple-emoji-map` ourselves instead. It is the exact package
 * flarum/emoji builds its map from, so `:heart:` means the same thing in a chat
 * reaction as in a post — consistency that matters more than the 55 KB.
 *
 * ## Why lazily
 *
 * 55 KB of JSON has no business in the main bundle for a feature most page views
 * never touch. The dynamic import puts it in its own chunk, and a small inline
 * table covers the common reaction set so the first paint is never blank.
 */

/**
 * The reactions the hover bar and the picker open with, plus a few obvious ones,
 * so the common case renders synchronously and the chunk load is invisible.
 */
const COMMON = {
  heart: "❤️",
  "+1": "👍",
  "-1": "👎",
  tada: "🎉",
  eyes: "👀",
  laughing: "😆",
  smile: "😄",
  cry: "😢",
  fire: "🔥",
  rocket: "🚀",
  thinking: "🤔",
  clap: "👏",
  speech_balloon: "💬",
  white_check_mark: "✅",
  x: "❌"
};

/** name → unicode, built by inverting the package's unicode → names map. */
let full = null;
let loading = false;

/**
 * Kicks off the chunk load. Safe to call repeatedly; only the first call fetches.
 *
 * Redraws on completion so anything already rendered from COMMON (or rendered as
 * a raw shortcode) upgrades in place.
 */
export function loadEmojiMap() {
  if (full || loading) return;
  loading = true;

  // `./emojiMap`, not the package by name: the chunk registration keys off a path
  // resolved relative to this file, so a bare package name yields an unregistered
  // chunk whose URL 404s. See the comment in emojiMap.ts.
  import(/* webpackChunkName: 'forum/utils/emojiMap', webpackMode: 'lazy-once' */ './emojiMap').then(module => {
    var _module$default;
    const source = (_module$default = module.default) != null ? _module$default : module;
    const inverted = {};
    for (const _ref of Object.entries(source)) {
      var _ref2 = _slicedToArray(_ref, 2);
      const unicode = _ref2[0];
      const names = _ref2[1];
      if (!Array.isArray(names)) continue;
      for (const name of names) {
        // First name wins: the package lists the canonical shortcode first, and
        // later aliases would otherwise overwrite it with the same value anyway.
        if (!(name in inverted)) inverted[name] = unicode;
      }
    }
    full = inverted;
    m.redraw();
  }).catch(() => {
    // Stay on COMMON. A missing chunk must not break the chat.
    full = {};
  }).finally(() => {
    loading = false;
  });
}

/** Strips the delimiters and whitespace from `:name:`. */
function normalise(input) {
  return input.trim().replace(/^:+|:+$/g, "");
}

/**
 * True when the string already contains an emoji (or any non-ASCII pictograph),
 * meaning it needs no translation.
 *
 * Deliberately loose: the goal is to distinguish "the user pasted 💬" from "the
 * user typed speech_balloon", not to police which codepoints qualify.
 */
export function looksLikeEmoji(input) {
  const value = input.trim();
  if (value === "" || value.length > 8) return false;
  return /(?:[\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26A7\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDC2C-\uDC2F\uDC94-\uDC9F\uDCAF\uDCB0\uDCC0\uDCCF\uDCD0\uDCF6-\uDCFF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDAE-\uDDFF\uDE01-\uDE0F\uDE1A\uDE2F\uDE32-\uDE3A\uDE3C-\uDE3F\uDE49-\uDE5F\uDE66-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA4\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDED5-\uDEE5\uDEE9\uDEEB-\uDEF0\uDEF3-\uDEFF\uDFDA-\uDFFF]|\uD83E[\uDC0C-\uDC0F\uDC48-\uDC4F\uDC5A-\uDC5F\uDC88-\uDC8F\uDCAE\uDCAF\uDCBC-\uDCBF\uDCC2-\uDCCF\uDCD9-\uDCFF\uDD0C-\uDD3A\uDD3C-\uDD45\uDD47-\uDDFF\uDE58-\uDE5F\uDE6E-\uDEFF]|\uD83F[\uDC00-\uDFFD])/.test(value);
}

/**
 * Resolves a shortcode, with or without colons, to a Unicode emoji.
 *
 * Returns the input unchanged when it is already an emoji, and `null` when the
 * shortcode is unknown — callers decide whether to show a fallback or nothing.
 */
export function resolveEmoji(input) {
  var _ref3, _full$name, _full;
  if (!input) return null;
  if (looksLikeEmoji(input)) return input.trim();
  const name = normalise(input).toLowerCase();
  if (name === "") return null;

  // Trigger the full map so an unknown-now shortcode resolves after it lands.
  loadEmojiMap();
  return (_ref3 = (_full$name = (_full = full) == null ? void 0 : _full[name]) != null ? _full$name : COMMON[name]) != null ? _ref3 : null;
}

/**
 * Renders an emoji for display, falling back to the raw shortcode so an unknown
 * value is visible and debuggable rather than silently blank.
 */
export function displayEmoji(input, fallback) {
  var _resolveEmoji;
  if (fallback === void 0) {
    fallback = "";
  }
  if (!input) return fallback;
  return (_resolveEmoji = resolveEmoji(input)) != null ? _resolveEmoji : ":".concat(normalise(input), ":");
}
/**
 * Searches the map by shortcode for the picker.
 *
 * Prefix matches rank above substring matches, so typing "hea" surfaces `heart`
 * before `broken_heart`. Results are capped because the dropdown is scrollable,
 * not infinite, and scoring 1949 entries on every keystroke is enough work
 * already.
 */
export function searchEmoji(query, limit) {
  if (limit === void 0) {
    limit = 48;
  }
  loadEmojiMap();
  const source = full && Object.keys(full).length > 0 ? full : COMMON;
  const term = normalise(query).toLowerCase();
  const prefix = [];
  const contains = [];
  for (const _ref4 of Object.entries(source)) {
    var _ref5 = _slicedToArray(_ref4, 2);
    const name = _ref5[0];
    const unicode = _ref5[1];
    if (term === "") {
      prefix.push({
        name,
        unicode
      });
      if (prefix.length >= limit) break;
      continue;
    }
    const at = name.indexOf(term);
    if (at === 0) {
      prefix.push({
        name,
        unicode
      });
    } else if (at > 0) {
      contains.push({
        name,
        unicode
      });
    }
    if (prefix.length >= limit) break;
  }
  return [...prefix, ...contains].slice(0, limit);
}

/** True once the full map is available, so the picker can say it is still loading. */
export function emojiMapReady() {
  return full !== null;
}

/**
 * Whether a value is acceptable as a channel's emoji. Mirrors the server-side
 * check in ChannelResource so the form rejects it before the request.
 */
export function isValidEmoji(input) {
  if (!input || input.trim() === "") return true; // empty is allowed

  return resolveEmoji(input) !== null;
}
flarum.reg.add('ramon-chat', 'forum/utils/emoji', { loadEmojiMap: loadEmojiMap,looksLikeEmoji: looksLikeEmoji,resolveEmoji: resolveEmoji,displayEmoji: displayEmoji,searchEmoji: searchEmoji,emojiMapReady: emojiMapReady,isValidEmoji: isValidEmoji, });flarum.reg.addChunkModule('135', '444', 'ramon-chat', 'forum/utils/emojiMap');